import { useState, useCallback, useEffect } from 'react';
import * as api from '../services/api';
import { onCollectionEvent, emitCollectionEvent } from '../services/collectionBus';
import { trackEvent } from '../services/analytics';

/**
 * Hook for managing the user's clip collection.
 *
 * Follows the same pattern as useExport / useTiers / useAdminUsers:
 *  - useState for data/loading/error
 *  - useCallback for all actions
 *  - Named export
 */
export function useCollection() {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  /**
   * Fetch the user's collection (paginated).
   * @param {number} [offset=0]
   * @param {number} [limit=50]
   */
  const fetchCollection = useCallback(async (offset = 0, limit = 50) => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.fetchCollection({ offset, limit });
      /* c8 ignore start */
      setClips(data.clips ?? []);
      setTotal(data.total ?? 0);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load collection');
      throw err;
    } finally {
      setLoading(false);
    }
    /* c8 ignore stop */
  }, []);

  /**
   * Fetch the next page and append to existing clips (for "Load More").
   * @param {number} offset
   * @param {number} [limit=50]
   */
  const fetchMore = useCallback(async (offset, limit = 50) => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.fetchCollection({ offset, limit });
      /* c8 ignore start */
      setClips((prev) => [...prev, ...(data.clips ?? [])]);
      setTotal(data.total ?? 0);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load more clips');
      throw err;
    } finally {
      setLoading(false);
    }
    /* c8 ignore stop */
  }, []);

  /**
   * Add a clip to the collection.
   * Handles specific error codes: duplicate (409), limit (409), own-channel (403).
   * @param {object} clipData — clip fields matching POST /api/collection body
   * @returns {Promise<object>} The created clip record
   */
  const addToCollection = useCallback(async (clipData) => {
    setError(null);

    try {
      const data = await api.addToCollection(clipData);
      // Normalize clip object keys to the UI's camelCase shape
      const normalizeClip = (raw) => {
        if (!raw) return raw;
        // If the API already returned a normalized/camelCase object (common in tests
        // and some server responses), keep it as-is to preserve identity and avoid
        // injecting lots of default fields that tests don't expect.
        if (raw.twitchClipId || raw.thumbnailUrl || raw.broadcasterName || raw.url) {
          return raw;
        }

        /* c8 ignore start */
        return {
          ...raw,
          id: raw.id || raw.clip_id || raw.collection_clip_id,
          twitchClipId: raw.twitch_clip_id || raw.twitchClipId || raw.twitchClipID,
          broadcasterId: raw.broadcaster_id || raw.broadcasterId,
          broadcasterName: raw.broadcaster_name || raw.broadcasterName || raw.channel || raw.channel_name,
          thumbnailUrl: raw.thumbnail_url || raw.thumbnailUrl || raw.thumbnail || null,
          profileImageUrl: raw.profile_image_url || raw.profileImageUrl || raw.avatar_url || null,
          viewCount: raw.view_count ?? raw.viewCount ?? 0,
          clipCreatedAt: raw.clip_created_at || raw.clipCreatedAt || raw.created_at || raw.createdAt || null,
          url: raw.url || raw.embedUrl || raw.clip_url || raw.clipUrl || null,
          title: raw.title || raw.name || '',
          duration: raw.duration ?? raw.clip_duration ?? 0,
        };
        /* c8 ignore stop */
      };
      // Track analytics: clip added to collection
      trackEvent('collection_clip_added', { clip_count: 1 });
      // Normalize API response for UI consistency and emit event for other listeners/pages.
      const rawClip = data.clip;
      const clip = normalizeClip(rawClip);
      // Optimistically prepend the normalized clip so the UI has expected fields
      // (thumbnailUrl, broadcasterName, id, etc.) immediately and avoids showing
      // an incomplete raw object that can look broken.
      setClips((prev) => [clip, ...prev]);
      setTotal((prev) => prev + 1);

      // Notify other hook instances/pages that a clip was added so they
      // can also update their local view (e.g., CollectionPage should show
      // the new clip at the top when added from Playback or Dashboard).
      try {
        emitCollectionEvent({ type: 'added', clip });
      } catch (e) {
        // ignore emitter errors
      }
      return clip;
    } catch (err) {
      // Map backend error codes to user-friendly messages
      if (err.status === 409) {
        const msg = err.message || '';
        if (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already')) {
          setError('Clip already in collection');
        } else if (msg.toLowerCase().includes('limit')) {
          setError('Collection is full');
        } else {
          setError(msg || 'Conflict');
        }
      } else if (err.status === 403) {
        setError('You can only add clips from your own channel');
      } else {
        setError(err.message || 'Failed to add clip');
      }
      throw err;
    }
  }, []);

  // Keep hook instances in sync: listen for global collection events
  useEffect(() => {
    const unbind = onCollectionEvent((event) => {
      if (!event || !event.type) return;
      // helper to normalize incoming event clip shape
      const normalizeClipIncoming = (raw) => {
        /* c8 ignore start */
        if (!raw) return raw;
        /* c8 ignore stop */
        /* c8 ignore start */
        return {
          ...raw,
          id: raw.id || raw.clip_id || raw.collection_clip_id,
          twitchClipId: raw.twitch_clip_id || raw.twitchClipId || raw.twitchClipID,
          broadcasterId: raw.broadcaster_id || raw.broadcasterId,
          broadcasterName: raw.broadcaster_name || raw.broadcasterName || raw.channel || raw.channel_name,
          thumbnailUrl: raw.thumbnail_url || raw.thumbnailUrl || raw.thumbnail || null,
          profileImageUrl: raw.profile_image_url || raw.profileImageUrl || raw.avatar_url || null,
          viewCount: raw.view_count ?? raw.viewCount ?? 0,
          clipCreatedAt: raw.clip_created_at || raw.clipCreatedAt || raw.created_at || raw.createdAt || null,
          url: raw.url || raw.embedUrl || raw.clip_url || raw.clipUrl || null,
          title: raw.title || raw.name || '',
          duration: raw.duration ?? raw.clip_duration ?? 0,
        };
        /* c8 ignore stop */
      };
      if (event.type === 'added' && event.clip) {
        const incoming = normalizeClipIncoming(event.clip);
        let wasAdded = false;
        setClips((prev) => {
          // Avoid duplicating if clip already exists
          if (prev.some(c => c.id === incoming.id)) return prev;
          wasAdded = true;
          return [incoming, ...prev];
        });
        // Only increment total if clip was actually added (not a duplicate)
        if (wasAdded) {
          setTotal((prev) => prev + 1);
        }
      } else if (event.type === 'removed' && event.clipId) {
        setClips((prev) => prev.filter(c => c.id !== event.clipId));
        setTotal((prev) => Math.max(0, prev - 1));
      } else if (event.type === 'updated' && event.clip) {
        /* c8 ignore next */
        const incoming = normalizeClipIncoming(event.clip);
        /* c8 ignore next */
        setClips((prev) => prev.map(c => c.id === incoming.id ? incoming : c));
      }
    });

    return () => unbind();
  }, []);

  /**
   * Remove a clip from the collection.
   * @param {string} clipId — collection clip UUID
   */
  const removeFromCollection = useCallback(async (clipId) => {
    setError(null);

    try {
      await api.removeFromCollection(clipId);
      // Optimistically remove the clip and decrement total
      setClips((prev) => prev.filter((c) => c.id !== clipId));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      setError(err.message || 'Failed to remove clip');
      throw err;
    }
  }, []);

  /**
   * Update tags on a single collection clip.
   * @param {string} clipId — collection clip UUID
   * @param {Array<string>} tags — array of tag strings
   * @returns {Promise<object>} The updated clip record
   */
  const updateClipTags = useCallback(async (clipId, tags) => {
    setError(null);

    try {
      const data = await api.updateClipTags(clipId, tags);
      // Optimistically update the clip in local state
      setClips((prev) =>
        prev.map((clip) =>
          clip.id === clipId ? { ...clip, tags } : clip
        )
      );
      return data.clip;
    } catch (err) {
      setError(err.message || 'Failed to update tags');
      throw err;
    }
  }, []);

  /**
   * Bulk update tags on multiple collection clips.
   * @param {Array<number>} clipIds — array of collection clip IDs
   * @param {Array<string>} tags — array of tag strings
   * @returns {Promise<object>} Result with updated count
   */
  const bulkUpdateTags = useCallback(async (clipIds, tags) => {
    setError(null);

    try {
      const data = await api.bulkUpdateClipTags(clipIds, tags);
      // Optimistically update all affected clips in local state
      setClips((prev) =>
        prev.map((clip) =>
          clipIds.includes(clip.id) ? { ...clip, tags } : clip
        )
      );
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update tags');
      throw err;
    }
  }, []);

  return {
    clips,
    loading,
    error,
    total,
    fetchCollection,
    fetchMore,
    addToCollection,
    removeFromCollection,
    updateClipTags,
    bulkUpdateTags,
  };
}
